import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Box, DialogContent, Typography } from '@mui/material'
import { FormProvider } from 'react-hook-form'

import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import { TOAST_PROPS_OPTIONS } from '@hapana/shared/web/components/shared/toast/toast.constants'
import {
  CLOSE_TYPES,
  SIZES,
} from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.constants'
import { styles as dialogStyles } from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.styles'
import Dialog from '@hapana/shared/web/components/shared/dialog/dialog.component'
import TextField from '@hapana/shared/web/components/shared/text-field/text-field.component'
import { useTrialMagicLink } from '_pages/demo/demo.hooks'

const EMAIL_FIELD = 'email'

const schema = z.object({
  [EMAIL_FIELD]: z.string().email('Invalid email address').min(1, 'Email is required'),
})

const SendLoginLink = ({ defaultEmail = '' }) => {
  const { t } = useTranslation('core')
  const { popDialog } = useDialog()
  const { openToast } = useToast()
  const { sendMagicLink } = useTrialMagicLink()
  const [isLoading, setIsLoading] = useState(false)
  const mountedRef = useRef(true)
  useEffect(
    () => () => {
      mountedRef.current = false
    },
    []
  )

  const formMethods = useForm({
    resolver: zodResolver(schema),
    defaultValues: { [EMAIL_FIELD]: defaultEmail },
    mode: 'onTouched',
  })

  const {
    handleSubmit,
    formState: { isValid },
  } = formMethods

  const onSubmit = async (data) => {
    if (mountedRef.current) {
      setIsLoading(true)
    }
    try {
      localStorage.setItem('trialEmailForSignIn', data[EMAIL_FIELD])
      await sendMagicLink(data[EMAIL_FIELD])
      if (mountedRef.current) {
        setIsLoading(false)
      }
      openToast({
        title: t('login.sendLoginLink.toastSuccessTitle'),
        description: t('login.sendLoginLink.toastSuccessDescription', { email: data[EMAIL_FIELD] }),
        severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
      })
      popDialog()
    } catch (error) {
      if (mountedRef.current) {
        setIsLoading(false)
      }
      openToast({
        title: t('login.sendLoginLink.toastErrorTitle'),
        description: error?.message || t('login.sendLoginLink.toastErrorFallback'),
        severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
      })
    }
  }

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)} style={dialogStyles.form}>
        <Dialog
          title={t('login.sendLoginLink.dialogTitle')}
          closeType={CLOSE_TYPES.ICON}
          size={SIZES.XS}
          hasMobileBottomSheet
          primaryAction={{
            label: t('login.sendLoginLink.submitLabel'),
            isLoading,
            action: handleSubmit(onSubmit),
            props: { disabled: !isValid || isLoading },
          }}
        >
          <DialogContent>
            <Typography typography="medium" color="gray.80">
              {t('login.sendLoginLink.description')}
            </Typography>
            <Typography typography="header4" color="gray.80">
              {t('login.sendLoginLink.noPasswordNeeded')}
            </Typography>
            <Box sx={dialogStyles.forgotPasswordWrapper}>
              <TextField
                label={t('login.sendLoginLink.emailLabel')}
                name={EMAIL_FIELD}
                id="login-link-email"
              />
            </Box>
          </DialogContent>
        </Dialog>
      </form>
    </FormProvider>
  )
}

export default SendLoginLink
